import React, { useEffect, useState } from "react";
import { Grid, Card, CardContent, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { configState } from "../../../recoil/atoms";
import { useRecoilValue } from "recoil";
import { StatisticsApi } from "../../../api/medical-operations";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { DiagnosesStatistics, GeneralStatistics } from "../../../api/medical-operations";
import {
  IconSearch,
  IconUser,
  IconGenderFemale,
  IconGenderMale
} from "@tabler/icons";

import Paper from '@mui/material/Paper';
import {
  Chart,
  PieSeries,  
  Tooltip,
  Legend
} from '@devexpress/dx-react-chart-material-ui';
import { EventTracker } from '@devexpress/dx-react-chart';
import { Animation } from '@devexpress/dx-react-chart';

const commonCardStyle = {
  borderRadius: 3,
  display: 'flex',
  justifyContent: 'space-around',
  gap: 2,
  alignItems: 'center',
  pr: 2,
  mr: 2,
  width: '100%'
};

export default function Diagnoses() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [diagnoses, setDiagnoses] = useState<DiagnosesStatistics[]>([]);
  const [statistics, setStatistics] = useState<GeneralStatistics[]>([]);

  const config = useRecoilValue(configState);

  const getDiagnoses = () => {
    const Diagnosis = new StatisticsApi(config);
    Diagnosis.getDiagnosisStatistics()
      .then((res) => {
        setIsLoading(false);        
        setDiagnoses(res.data.DiagnosesStatistics || []); 
        setStatistics(res.data.GeneralStatistics || []);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getDiagnoses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  

  return (    
    <>   
    {isLoading ? (
      <LinearProgress color="secondary" />
      ) : (
      <>              
        <Box display="flex" sx={{ mb: 2, flexWrap: window.innerWidth <= 700 ? "wrap" : "nowrap", gap: 0.5 }}>
          {statistics.length > 0 ? (
            <>
              <Card sx={commonCardStyle}>
                <CardContent>
                  <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" component="div">
                      Total Number of Patients
                    </Typography>
                    <Typography variant="h2">
                      {statistics[0].totalNumberOfPatients}
                    </Typography>
                  </Box>
                </CardContent>
                <Typography style={{ fontSize: '40px' }}>
                  <IconUser />
                </Typography>
              </Card>
              <Card sx={commonCardStyle}>
                <CardContent>
                  <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" component="div">
                      Number of Males
                    </Typography>
                    <Typography variant="h2">
                      {statistics[0].numberOfMales}
                    </Typography>
                  </Box>
                </CardContent>
                <Typography style={{ fontSize: '40px' }}>
                  <IconGenderMale />
                </Typography>
              </Card>
              <Card sx={commonCardStyle}>
                <CardContent>
                  <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" component="div">
                      Number of Females
                    </Typography>
                    <Typography variant="h2">
                      {statistics[0].numberOfFemales}
                    </Typography>
                  </Box>
                </CardContent>
                <Typography style={{ fontSize: '40px' }}>
                  <IconGenderFemale />
                </Typography>
              </Card>
              <Card sx={commonCardStyle}>
                <CardContent>
                  <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" component="div">
                      Number of Unspecified Gender
                    </Typography>
                    <Typography variant="h2">
                      {statistics[0].numberOfUnspecifiedGender}
                    </Typography>
                  </Box>
                </CardContent>
                <Typography style={{ fontSize: '40px' }}>
                  <IconSearch />
                </Typography>
              </Card>
            </>
          ) : (
            <LinearProgress color="secondary" />
          )}
        </Box>
        <Typography variant="h4" gutterBottom sx={{ p: 1}}>
          Diagnoses Statistics  
        </Typography>    
        <Grid container spacing={2}>                            
            {diagnoses.map((diagnosis, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{borderRadius: 3, pt: 2}}>    
                  <Typography variant="h3" sx={{textAlign: "center", pt: 1}}>
                    {diagnosis.name}
                  </Typography> 
                  <Paper>
                    <Chart 
                      data={[
                        { property: 'Under 20', distribution: diagnosis.under20 },
                        { property: '20 to 40', distribution: diagnosis["20to40"] },
                        { property: '41 to 60', distribution: diagnosis["41to60"] },
                        { property: 'Over 60', distribution: diagnosis.over60 },
                      ]}
                      height={300}                      
                    >
                      <PieSeries
                        valueField="distribution"
                        argumentField="property"
                        innerRadius={0.3}
                        outerRadius={0.5}
                      />      
                      <Legend 
                        position="bottom"
                        rootComponent={(props) => {                          
                          return (
                            <div style={{ display: 'flex', 
                            justifyContent: 'center', width: "100%",
                            flexWrap: window.innerWidth <= 1400 ? "wrap" : "nowrap"  
                            }}>
                              {props.children}
                            </div>
                          );
                        }}
                      />
                      <EventTracker />
                      <Tooltip />
                      <Animation />      
                    </Chart>        
                  </Paper>                         
                  <CardContent>  
                    <Box sx={{pb: 2}}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Females</Typography>
                        <Typography>{diagnosis.Female} ({diagnosis.FemalePercentage}%)</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={diagnosis.FemalePercentage} />
                    </Box>
                    <Box sx={{pb: 2}}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Males</Typography>
                        <Typography>{diagnosis.Male} ({diagnosis.MalePercentage}%)</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={diagnosis.MalePercentage} />
                    </Box>
                    <Box sx={{pb: 2}}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>UnSpecified</Typography>
                        <Typography>{diagnosis.Unspecified} ({diagnosis.UnspecifiedPercentage}%)</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={diagnosis.UnspecifiedPercentage} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}        
        </Grid>
      </>
      )}
    </>
  );
}
